<script setup lang="ts">
import WrButton from "~/components/global/WrButton.vue";
import WrIcon from "../global/WrIcon.vue";

const colorMode = useColorMode();
const isLight = computed(() => colorMode.value !== "dark");

interface CompareCard {
  title: string;
  description: string;
  svgIcon?: string;
}

const emit = defineEmits<{ "cta-clicked": [] }>();

withDefaults(
  defineProps<{
    heading: string;
    subtitle: string;
    leftTitle: string;
    leftCards: CompareCard[];
    rightTitle: string;
    rightCards: CompareCard[];
    eyebrow?: string;
    ctaText?: string;
    ctaLink?: string | null;
  }>(),
  {
    eyebrow: "The difference",
    ctaText: "Build with Flutter",
    ctaLink: null,
  },
);
</script>

<template>
  <section class="relative">
    <div class="text-center">
      <span class="fl-eyebrow"><span class="fl-eyebrow-dot" />{{ eyebrow }}</span>
      <h2 class="fl-heading">{{ heading }}</h2>
      <p class="fl-subcopy" :class="isLight ? 'text-black opacity-70' : 'text-white opacity-70'">{{ subtitle }}</p>
    </div>

    <div class="mt-10 lg:mt-14 grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 px-4 max-w-[1000px] mx-auto">
      <!-- Left: the problem -->
      <div class="fl-col fl-col-left">
          <div class="fl-col-head">
            <span class="fl-dot fl-dot-left" />
            <h3 class="fl-col-title fl-col-title-left">{{ leftTitle }}</h3>
          </div>
          <div class="space-y-4">
            <div v-for="card in leftCards" :key="card.title" class="fl-row">
                <span class="fl-row-ic fl-row-ic-left">
                  <WrIcon :name="card.svgIcon || 'streamline:interface-close-cross-x-close-cancel'" class="size-3.5 text-[#8a8fa3]" />
                </span>
                <div>
                  <h4 class="fl-row-title fl-row-title-left">{{ card.title }}</h4>
                <p class="fl-row-desc fl-row-desc-left">{{ card.description }}</p>
              </div>
            </div>
          </div>
      </div>

      <!-- Right: the answer -->
      <div class="fl-col fl-col-right">
          <div class="absolute inset-0 -z-10 fl-col-glow" />
          <div class="fl-col-head">
            <span class="fl-dot fl-dot-right" />
            <h3 class="fl-col-title fl-col-title-right">{{ rightTitle }}</h3>
          </div>
          <div class="space-y-4">
            <div v-for="card in rightCards" :key="card.title" class="fl-row">
                <span class="fl-row-ic fl-row-ic-right">
                  <WrIcon :name="card.svgIcon || 'streamline:interface-check-validation-check-tick-approve'" class="size-3.5 text-[#12DBFF]" />
                </span>
                <div>
                  <h4 class="fl-row-title fl-row-title-right">{{ card.title }}</h4>
                <p class="fl-row-desc fl-row-desc-right">{{ card.description }}</p>
              </div>
            </div>
          </div>
      </div>
    </div>

    <div class="flex justify-center mt-10 lg:mt-12">
      <WrButton
        :link="ctaLink || undefined"
        @click="!ctaLink ? emit('cta-clicked') : undefined"
        container-class="h-[44px] lg:h-[48px]"
        append-class="text-sm lg:font-normal px-5 lg:px-6"
      >
        {{ ctaText }}
      </WrButton>
    </div>
  </section>
</template>

<style scoped>
.fl-eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.2em;
  color: #887fff; margin-bottom: 14px;
}
.fl-eyebrow-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: #ba55fe; box-shadow: 0 0 8px rgba(186, 85, 254, 0.8);
  animation: fl-blink 2.4s ease-in-out infinite;
}
@keyframes fl-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
@media (min-width: 1024px) { .fl-eyebrow { font-size: 12px; } }

.fl-heading {
  font-family: "ITC Avant Garde Gothic Demi", "ITC Avant Garde Gothic", "AvantGarde", sans-serif;
  font-size: clamp(28px, 4vw, 48px); font-weight: 600; line-height: 1.1; text-align: center;
  background: linear-gradient(92deg, #12dbff -3.57%, #ba55fe 80.72%);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; color: transparent;
}
.fl-subcopy {
  font-family: "ITC Avant Garde Gothic", "AvantGarde", sans-serif;
  font-size: 16px; line-height: 1.6; letter-spacing: 0.36px; margin-top: 16px;
  text-align: center; max-width: 720px; margin-left: auto; margin-right: auto; padding: 0 16px;
}
@media (min-width: 1024px) { .fl-subcopy { font-size: 18px; } }

.fl-col {
  position: relative; overflow: hidden; height: 100%;
  border-radius: 18px; padding: 20px;
}
@media (min-width: 1024px) { .fl-col { padding: 24px; } }
.fl-col-left {
  border: 1px solid rgba(136, 127, 255, 0.15);
  background: rgba(18, 0, 44, 0.3);
}
.fl-col-right {
  border: 1px solid rgba(186, 85, 254, 0.35);
  background: rgba(35, 27, 255, 0.08);
  box-shadow: 0 0 60px -20px rgba(186, 85, 254, 0.4);
}
.fl-col-glow {
  background: radial-gradient(ellipse 400px 200px at 50% 0%, rgba(186, 85, 254, 0.16), transparent 70%);
  animation: fl-glow-pulse 4s ease-in-out infinite;
}
@keyframes fl-glow-pulse { 0%, 100% { opacity: 0.7; } 50% { opacity: 1; } }

.fl-col-head { display: flex; align-items: center; gap: 10px; margin-bottom: 20px; }
.fl-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.fl-dot-left { background: #5b6478; }
.fl-dot-right {
  background: #ba55fe;
  box-shadow: 0 0 12px rgba(186, 85, 254, 0.7), 0 0 24px rgba(186, 85, 254, 0.3);
  animation: fl-dot-pulse 2s ease-in-out infinite;
}
@keyframes fl-dot-pulse {
  0%, 100% { box-shadow: 0 0 12px rgba(186, 85, 254, 0.7), 0 0 24px rgba(186, 85, 254, 0.3); transform: scale(1); }
  50% { box-shadow: 0 0 16px rgba(186, 85, 254, 0.9), 0 0 32px rgba(186, 85, 254, 0.45); transform: scale(1.15); }
}
.fl-col-title { font-size: 16px; font-weight: 600; }
@media (min-width: 1024px) { .fl-col-title { font-size: 18px; } }
.fl-col-title-left { color: #9aa0b4; }
.fl-col-title-right { color: #fff; }

.fl-row { display: flex; align-items: flex-start; gap: 12px; }
.fl-row-ic {
  width: 24px; height: 24px; margin-top: 2px; flex-shrink: 0; border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  transition: transform 0.3s ease;
}
.fl-row:hover .fl-row-ic { transform: scale(1.1); }
.fl-row-ic-left { background: rgba(136, 127, 255, 0.08); border: 1px solid rgba(136, 127, 255, 0.15); }
.fl-row-ic-right { background: rgba(186, 85, 254, 0.15); border: 1px solid rgba(186, 85, 254, 0.35); }
.fl-row-title { font-size: 15px; font-weight: 600; }
.fl-row-title-left { color: #c2c6d4; }
.fl-row-title-right { color: #fff; }
.fl-row-desc { font-size: 13px; line-height: 1.55; margin-top: 4px; }
@media (min-width: 1024px) { .fl-row-desc { font-size: 14px; } }
.fl-row-desc-left { color: #8a8fa3; }
.fl-row-desc-right { color: #bcb2c3; }

/* Light theme */
html:not(.my-app-dark) .fl-eyebrow { color: #613faa; }
html:not(.my-app-dark) .fl-col-left {
  background: #fff;
  border-color: rgba(208, 208, 208, 0.5);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}
html:not(.my-app-dark) .fl-col-right {
  background: #fff;
  border-color: rgba(186, 85, 254, 0.35);
  box-shadow: 0 8px 24px rgba(166, 85, 246, 0.15);
}
html:not(.my-app-dark) .fl-col-title-left { color: #555; }
html:not(.my-app-dark) .fl-col-title-right { color: #23216c; }
html:not(.my-app-dark) .fl-row-ic-left { background: rgba(0, 0, 0, 0.04); border-color: rgba(0, 0, 0, 0.08); }
html:not(.my-app-dark) .fl-row-ic-right {
  background: linear-gradient(135deg, rgba(18, 219, 255, 0.15) 0%, rgba(186, 85, 254, 0.2) 100%);
  border-color: transparent;
}
html:not(.my-app-dark) .fl-row-ic-right :deep(svg) { color: #7c3aed; }
html:not(.my-app-dark) .fl-row-title-left { color: #555; }
html:not(.my-app-dark) .fl-row-title-right { color: #23216c; }
html:not(.my-app-dark) .fl-row-desc-left { color: #777; }
html:not(.my-app-dark) .fl-row-desc-right { color: #444; }
</style>
